<template>
  <cvc-card
      :loading="false"
      :failed="false"
      :skeleton-type-matrix="[]"
      :skeleton-row-heights="[]">
    <v-row>
      <v-col>
        <v-row class="ma-1 pa-3">
          <v-col cols="3">
            <div class="text--secondary font-size-medium mt-2">
              Name:
            </div>
            <v-text-field
              required dense single-line
              class="profile-builder-name mt-1"
              v-model="profileName"
              :error-messages="profileNameErrors"
              @input="() => {$v.profileName.$touch(); responseErrors.profileName = []}"
              @blur="() => {$v.profileName.$touch(); responseErrors.profileName = []}"/>
          </v-col>

          <v-col cols="3">
            <div class="text--secondary font-size-medium">
              Update Every:
            </div>
            <cvc-frequency-input
              class="mt-2"
              v-model="updateEvery"
              :items="[{text: 'weeks', value: 7}, {text: 'days', value: 1}]"
              :errors="updateEveryErrors"
              />
          </v-col>

          <v-col cols="3">
            <div class="text--secondary font-size-medium">
              Expiry:
            </div>
            <cvc-date-input
              class="mt-3"
              v-model="expires"
              :errors="expiresErrors"
              :clearable="false"
              :allowed-dates="allowedExpiryDates"/>
          </v-col>

          <v-col cols="3" class="pb-0">
            <div class="text--secondary font-size-medium mt-2">
              Description:
            </div>
            <v-textarea
              required dense no-resize rows="2"
              class="profile-builder-name mt-1"
              v-model="profileDescription"
              :error-messages="profileDescriptionErrors"
              @input="() => {$v.profileDescription.$touch(); responseErrors.profileDescription = []}"
              @blur="() => {$v.profileDescription.$touch(); responseErrors.profileDescription = []}"/>
          </v-col>
        </v-row>
        <v-row class="ma-1 pa-3">
          <v-col class="profile-builder-query">
            <div class="text--secondary mt-6" v-if="logicText">
              {{logicText}}
            </div>
            <query-builder
              class="mt-6" ref="queryBuilder"
              :initial-query="query"
              :on-query-change="onChangeQuery"/>
          </v-col>
        </v-row>
        <v-row>
          <v-btn
            class="cvc-button" style="width: 270px"
            depressed right
            :disabled="$v.$invalid || queryErrors.length > 0 || otherProfileNames.some(n => n === profileName)"
            @click="postProfile">
            Submit
          </v-btn>
        </v-row>
      </v-col>
    </v-row>
  </cvc-card>
</template>

<script>
import CvcCard from "@/components/CvcCard";
import {validationMixin} from "vuelidate";
import {required} from "vuelidate/lib/validators";
import CvcDateInput from "@/components/CvcFields/CvcDateInput";
import CvcFrequencyInput from "@/components/CvcFields/CvcFrequencyInput";
import QueryBuilder from "@/components/ProfileBuilder/QueryBuilder";
import {
  isAlphanumeric,
  isAlphanumericOrWhitespace,
  getRandomKebabCase,
  toTitleCase
} from "@/helpers/TextUtils";
import {parseISO, endOfToday} from "date-fns";
import CvcqlBuilderIds from "@/helpers/CvcqlBuilderIds";
import CvcqlJsonSchema from "@/helpers/CvcqlJsonSchema";
import { buildProfile } from "@/helpers/Cvcql";


const today = endOfToday()

export default {
  name: 'NotificationsSegments',
  components: {
    QueryBuilder,
    CvcFrequencyInput,
    CvcDateInput,
    CvcCard,
  },
  mixins: [validationMixin],
  validations: {
    profileName: { required, isAlphanumeric, allowedName: x => x.toLowerCase() !== 'home' && x.toLowerCase() !== 'work'},
    updateEvery: { required },
    query: { required },
    profileDescription: {isAlphanumericOrWhitespace}
  },
  data() {
    return {
      profileId: null,
      profileName: '',
      expires: null,
      updateEvery: 7,
      profileDescription: '',
      query: null,
      queryErrors: [],
      logicText: '',
      existingProfileIds: [],
      otherProfileNames: [],
      responseErrors: {}
    }
  },
  created() {
    CvcqlBuilderIds.resetIds();
    const selectedProfile = this.$store.state.profile.selectedProfile;

    this.otherProfileNames = this.$store.getters.nonDeletedProfiles.map(p => p.name);
    if (this.$route.query.edit && selectedProfile) {
      this.profileId = selectedProfile.metadata.name;
      this.profileName = selectedProfile.metadata.annotations && selectedProfile.metadata.annotations['insights/display-name'] ?
          selectedProfile.metadata.annotations['insights/display-name'] : toTitleCase(selectedProfile.metadata.name);
      this.profileDescription = selectedProfile.metadata.annotations && selectedProfile.metadata.annotations['insights/description'] ?
          selectedProfile.metadata.annotations['insights/description'] : '';

      this.otherProfileNames = this.$store.getters.nonDeletedProfiles
          .filter(p => p.id !== this.profileId)
          .map(p => p.name);

      if (selectedProfile.metadata.expires) this.expires = selectedProfile.metadata.expires;
      if (selectedProfile.metadata.update_every) this.updateEvery = selectedProfile.metadata.update_every;
      this.query = selectedProfile.query
      this.queryErrors = CvcqlJsonSchema.validate(this.query).errors;
    } else {
      this.existingProfileIds = this.$store.getters.existingProfileIds;
    }
  },
  mounted() {
    this.$v.$reset();
    this.logicText = this.$refs.queryBuilder.getLogicText();
  },
  beforeRouteLeave (to, from, next) {
    if (!this.$v.$anyDirty || confirm('Unsaved changes will be lost')) {
      next();
    } else {
      next(false);
    }
  },
  watch: {
    updateEvery(val) {
      this.$v.updateEvery.$touch();
      this.responseErrors.updateEvery = []
    },
    expires(val) {
      this.responseErrors.expires = []
    },
  },
  computed: {
    profileNameErrors() {
      const errors = [];
      if (!this.$v.profileName.$dirty) return errors;
      if (!this.$v.profileName.required) errors.push('Profile name is required');
      if (!this.$v.profileName.isAlphanumeric) errors.push('Profile name must be a-z, A-Z, 0-9 and spaces.');
      if (!this.$v.profileName.allowedName) errors.push("Profile name must not be called 'home' or 'work'.");
      if (this.otherProfileNames.some(n => n === this.profileName)) errors.push('Segment name all ready exists.');
      return errors.concat(this.responseErrors.profileName || []);
    },
    updateEveryErrors() {
      const errors = [];
      if (!this.$v.updateEvery.$dirty) return errors;
      if (!this.$v.updateEvery.required) errors.push('Update Every is required');
      return errors.concat(this.responseErrors.updateEvery || []);
    },
    expiresErrors() {
      return this.responseErrors.expires || [];
    },
    profileDescriptionErrors() {
      const errors = [];
      if (!this.$v.profileDescription.$dirty) return errors;
      if (!this.$v.profileDescription.isAlphanumericOrWhitespace)
        errors.push('Profile description can only consist of alphanumeric, whitespace and fullstop characters.');
      return errors.concat(this.responseErrors.profileDescription || []);
    },
  },
  methods: {
    postProfile() {
      const data = this.getFormData();
      this.$store.dispatch('postProfile', {
        data,
      }).then(() => {
        this.$v.$reset();
        this.$router.push({name: 'AdvertisingSegments'});
      }).catch((error) => {
        this.responseErrors = {
          profileName: error.response.data.metadata.profileName,
          expires: error.response.data.metadata.expires,
          updateEvery: error.response.data.metadata.update_every,
          query: error.response.data.metadata.query,
        }
      });
    },
    allowedExpiryDates(val) {
      const expiryDate = parseISO(val);
      return expiryDate > today;
    },
    onChangeQuery() {
      this.$nextTick(() => {
        this.query = this.$refs.queryBuilder.getQuery();
        this.queryErrors = CvcqlJsonSchema.validate(this.query).errors;
        this.logicText = this.$refs.queryBuilder.getLogicText();
        this.$v.query.$touch();
        this.responseErrors.query = [];
      });
    },
    getFormData() {
      if (!this.profileId) {
        this.profileId = getRandomKebabCase(this.profileName, this.existingProfileIds);
      }

      let optionalFields = {};
      if (this.expires !== null) optionalFields['expires'] = this.expires + "T00:00:00";
      if (this.updateEvery !== null) optionalFields['update_every'] = this.updateEvery;

      let optionalAnnotations = {};
      if (this.profileDescription) optionalAnnotations["insights/description"] = this.profileDescription;

      return buildProfile(this.query, {
        name: this.profileId,
        annotations: {
            "insights/display-name": this.profileName,
            ...optionalAnnotations,
        },
        ...optionalFields
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "src/styles/_mixins.scss";

  .profile-builder-name {
    max-width: 320px;
  }
  .profile-builder-query {
      border-top: 1px solid lightgray !important;
      min-height: 500px;
  }
</style>
